import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { isApiError } from './api'
import { useApi } from './api-context'
import { readTokenClaims, type AuthClient, type TokenClaims } from './auth'
import { errorMessage } from './errors'
import {
  PROFILE_QUERY_KEY,
  PUBLIC_QUERY_SCOPE,
  SessionContext,
  isUsableClinic,
  type Profile,
  type Session,
} from './session'
import { clearStoredFilters } from './use-stored-filter'

const CLAIMS_QUERY_KEY = ['session-claims'] as const

/** Liga o Supabase Auth ao `GET /me` e entrega a sessão às páginas (`useSession`). */
export function SessionProvider({ auth, children }: { auth: AuthClient; children: ReactNode }) {
  const api = useApi()
  const queryClient = useQueryClient()
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null)
  // A pessoa acabou de entrar por esta aba e ainda não escolheu a clínica (RN-AUT-11). Fica só em
  // memória: recarregar a página não pergunta de novo (P-AUT-18).
  const [isFreshSignIn, setIsFreshSignIn] = useState(false)
  // A senha foi definida nesta entrada: a janela do código já serviu (RN-AUT-07).
  const [isCodeWindowClosed, setIsCodeWindowClosed] = useState(false)

  useEffect(() => {
    let active = true
    void auth.getAccessToken().then((token) => {
      if (active) setIsSignedIn((current) => current ?? Boolean(token))
    })
    const stop = auth.onChange((signedIn) => {
      if (!active) return
      setIsSignedIn(signedIn)
      if (!signedIn) {
        setIsFreshSignIn(false)
        setIsCodeWindowClosed(false)
      }
    })
    return () => {
      active = false
      stop()
    }
  }, [auth])

  // A marca de troca obrigatória da senha vem do token: com ela, `GET /me` responderia 403 (RT-04).
  const claimsQuery = useQuery({
    queryKey: CLAIMS_QUERY_KEY,
    queryFn: async () => readTokenClaims(await auth.getAccessToken()),
    enabled: isSignedIn === true,
    staleTime: Infinity,
    retry: false,
  })
  const mustChangePassword = claimsQuery.data?.mustChangePassword
  const codeSignInAt = claimsQuery.data?.codeSignInAt ?? null

  const profileQuery = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: () => api.get<Profile>('/me'),
    enabled: isSignedIn === true && mustChangePassword === false,
    staleTime: 60_000,
    retry: false,
  })

  /** Sessão aberta: o token novo é lido e o perfil, pedido de novo. */
  const entered = useCallback(async () => {
    setIsSignedIn(true)
    setIsFreshSignIn(true)
    setIsCodeWindowClosed(false)
    // Entrar por cima de outra sessão (convite aberto com outra conta ativa): o que estava em cache
    // era dela. O que as telas públicas leram antes da sessão continua valendo.
    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] !== CLAIMS_QUERY_KEY[0] &&
        query.queryKey[0] !== PROFILE_QUERY_KEY[0] &&
        query.queryKey[0] !== PUBLIC_QUERY_SCOPE,
    })
    await queryClient.invalidateQueries({ queryKey: CLAIMS_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY })
  }, [queryClient])

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await auth.signIn(email, password)
      if (!result.error) await entered()
      return result
    },
    [auth, entered],
  )

  const verifyCode = useCallback(
    async (email: string, code: string) => {
      const result = await auth.verifyCode(email, code)
      if (!result.error) await entered()
      return result
    },
    [auth, entered],
  )

  const signOut = useCallback(async () => {
    await auth.signOut()
    setIsSignedIn(false)
    setIsFreshSignIn(false)
    setIsCodeWindowClosed(false)
    queryClient.clear()
    // Quem entrar depois nesta aba começa sem os filtros de quem saiu.
    clearStoredFilters()
  }, [auth, queryClient])

  const switchClinic = useCallback(
    async (clinicId: string | null, options: { isDefault?: boolean } = {}) => {
      await api.put('/me/clinic', options.isDefault ? { clinicId, isDefault: true } : { clinicId })
      await auth.refresh()
      // A escolha desta entrada está feita (RN-AUT-11).
      setIsFreshSignIn(false)
      // O perfil é relido na hora; `clear()` deixaria a tela com o perfil antigo até recarregar.
      await queryClient.refetchQueries({ queryKey: PROFILE_QUERY_KEY })
      // Outra clínica, outros dados: nada do cache anterior vale, e os filtros recomeçam.
      clearStoredFilters()
      void queryClient.resetQueries({
        predicate: (query) =>
          query.queryKey[0] !== PROFILE_QUERY_KEY[0] && query.queryKey[0] !== CLAIMS_QUERY_KEY[0],
      })
    },
    [api, auth, queryClient],
  )

  const refresh = useCallback(async () => {
    await auth.refresh()
    await queryClient.refetchQueries({ queryKey: CLAIMS_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY })
  }, [auth, queryClient])

  const passwordChanged = useCallback(async () => {
    await auth.refresh()
    await queryClient.refetchQueries({ queryKey: CLAIMS_QUERY_KEY })
    if (queryClient.getQueryData<TokenClaims>(CLAIMS_QUERY_KEY)?.mustChangePassword) {
      // A senha mudou, mas o token não renovou: com a marca antiga a API recusaria tudo. A pessoa
      // entra de novo, já com a senha nova.
      await signOut()
      return
    }
    setIsCodeWindowClosed(true)
    await queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY })
  }, [auth, queryClient, signOut])

  const session = useMemo<Session>(() => {
    const profile = profileQuery.data ?? null
    const failure = profileQuery.error
    let status: Session['status'] = 'loading'
    if (isSignedIn === false) status = 'signed_out'
    else if (isSignedIn && mustChangePassword) status = 'password_required'
    else if (isSignedIn && profile) status = 'signed_in'
    else if (isSignedIn && failure) status = 'signed_out'
    const role = profile?.role
    // 401 é sessão vencida (o login já explica); qualquer outra recusa do perfil tem causa a mostrar.
    const refused = status === 'signed_out' && isSignedIn === true && failure
    const hasChoice =
      Boolean(profile?.isRoot) || (profile?.clinics.filter(isUsableClinic).length ?? 0) > 1
    return {
      status,
      profile: status === 'signed_in' ? profile : null,
      problem:
        refused && !(isApiError(failure) && failure.status === 401) ? errorMessage(failure) : null,
      isGlobalRoot: status === 'signed_in' && role === 'root' && !profile?.clinicId,
      canManageClinic:
        status === 'signed_in' &&
        Boolean(profile?.clinicId) &&
        (role === 'manager' || role === 'root'),
      mustChooseClinic: status === 'signed_in' && isFreshSignIn && hasChoice,
      codeSignInAt: isSignedIn && !isCodeWindowClosed ? codeSignInAt : null,
      signIn,
      verifyCode,
      signOut,
      switchClinic,
      refresh,
      passwordChanged,
    }
  }, [
    isSignedIn,
    isFreshSignIn,
    isCodeWindowClosed,
    mustChangePassword,
    codeSignInAt,
    profileQuery.data,
    profileQuery.error,
    signIn,
    verifyCode,
    signOut,
    switchClinic,
    refresh,
    passwordChanged,
  ])

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
}
